import { ReportStatus } from '@features/siteStatusV2/constants';
import { convertDateFormat } from '@features/siteStatusV2/utils/helpers';
import theme from '@theme';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Icon } from '../icon';
import InformationsContainer from './InformationContainer';
import { MapStatusToColor, MapStatusToDisplay } from './maps';
import ReportCardContainer from './ReportCardContainer';
import ReportTag from './ReportTag';
import Separator from './Separator';
import { TranslationNamespaces } from '../../../../locales/constants/translationNamespaces.enum';

interface ReportElementProps {
  id: string;
  date: string;
  type: string;
  status: ReportStatus;
  rawStatus: string;
  handleSeeButtonClick: (reportId: string) => void;
}

const ReportElement: React.FC<ReportElementProps> = ({
  id,
  date,
  type,
  status,
  rawStatus,
  handleSeeButtonClick
}) => {
  const { t } = useTranslation(TranslationNamespaces.SITE_STATUS);
  const statusColor = MapStatusToColor[status];
  const statusDisplay =
    status === ReportStatus.NO_STATUS ? rawStatus : t(MapStatusToDisplay[status]);
  return (
    <ReportCardContainer>
      <InformationsContainer>
        <ReportTag color="orange" full={true} label={type} icon={{ iconName: 'rss' }} />
        <Separator />
        <ReportTag color={statusColor} full={true} label={statusDisplay} />
        <Separator />
        <ReportTag color="grey" full={true} label={id} />
        <Separator />
        <DisplayDate>
          <CustomIcon iconName="solid-clock" />
          {t('report.creationDate')}
          {convertDateFormat(date)}
        </DisplayDate>
      </InformationsContainer>
      <ActionButton onClick={() => handleSeeButtonClick(id)}>{t('report.seeButton')}</ActionButton>
    </ReportCardContainer>
  );
};

const DisplayDate = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.563rem;
  line-height: 0.75rem;
  font-weight: 500;
  color: ${theme.palette.grey[400]};
`;

const CustomIcon = styled(Icon)`
  width: 0.563rem;
  margin-right: 0.125rem;
`;

const ActionButton = styled.button`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${theme.fonts.size.xs};
  line-height: 1.25rem;
  font-weight: ${theme.fonts.weight.semibold};
  background-color: white;
  border-width: 0;
  color: ${theme.palette.blue[500]};

  &:hover {
    color: ${theme.palette.blue[700]};
  }
`;

export default ReportElement;
